import localization from "../../../../config/localization";
import InfiniteScroll from "../../../../components/ui/InfiniteScroll";
import TransactionCard from "../../../../components/ui/TransactionCard";
import TransactionFilter from "./TransactionFilters";
import { useEffect, useState } from "react";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { TransactionStatusTypes } from "../../../../models/constants/transactionStatusTypes";
import { SportsHistoryDetails } from "../../models/response/sportsHistoryListResponse";
import { UserHistoryListRequest } from "../../../../models/Request/userHistoryListRequest";
import {
  getSportsBetHistoryDetails,
  getSportsHistory,
} from "../../services/transactions.service";
import {
  BetHistory,
  SportsHistorySummaryResponse,
} from "../../models/response/sportsHistorySummaryResponse";
import { SPORTS_BET_TYPES } from "../../../../models/constants/commonConstants";
import {
  SPORTS_BET_NAMES,
  SYSTEM_BITMAPS,
  FOLD_NAMES,
} from "../../../../models/constants/sportsBetConsts";
import { BetByDetailsRequest } from "../../models/request/betByDetailsRequest";
import { changeLoaderState } from "../../../../reducers/appStateReducer";
import { useAppDispatch } from "../../../../store/hooks";
import { SPORTS_STATUS_STYLE } from "../../models/constants/statusStyles";
import SingleBet from "../sports-bet/SingleBet";
import ComboBet from "../sports-bet/ComboBet";
import SystemBet from "../sports-bet/SystemBet";

export default function Sports() {
  const dispatch = useAppDispatch();
  type Filters = {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };

  const Default_Filters: Filters = {
    searchText: null,
    fromDate: null,
    toDate: null,
    statuses: [] as string[],
  };

  const [loading, setLoading] = useState(true);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [sportsHistoryData, setSportsHistoryData] = useState<
    SportsHistoryDetails[]
  >([]);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFilters] = useState<Filters>(Default_Filters);

  const [betHistoryData, setBetHistoryData] = useState<
    Record<string, BetHistory | null>
  >({});

  const [sysBetsArray, setSysBetsArray] = useState<
    Record<string, SportsHistorySummaryResponse[][]>
  >({});

  const [betStrings, setBetStrings] = useState<Record<string, string[]>>({});

  const statusValueMap: Record<string, string> = {
    open: "open,bet",
    win: TransactionStatusTypes.WIN,
    lost: TransactionStatusTypes.LOSS,
    refund: TransactionStatusTypes.REFUND,
    not_accepted: "betdiscard",
    half_win: "half-won",
    half_lost: "half-lost",
    cashout: TransactionStatusTypes.CASHOUT,
  };

  const handleOpenRowIds = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) => {
      if (isOpen) {
        if (!prev.includes(itemId)) {
          return [...prev, itemId];
        }
        return prev;
      } else {
        return prev.filter((id) => id !== itemId);
      }
    });
  };

  function createSportsHistoryRequest(
    pageNo: number,
    filters: {
      searchText: string | null;
      fromDate: string | null;
      toDate: string | null;
      statuses: string[];
    },
  ): UserHistoryListRequest {
    return {
      pageNo: pageNo,
      pageSize: 20,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      transactionId: filters.searchText?.trim() || null,
      status: filters.statuses.length
        ? filters.statuses
          .map((x) => statusValueMap[x.toLowerCase().replace(" ", "_")])
          .filter(Boolean)
          .join(",")
        : null,
      isCallFromMobileDevice: false,
    };
  }

  const handleFiltersChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const fetchData = async (isReset = false, appliedFilters = filters) => {
    if (isLoadingMore && !isReset) {
      return;
    }

    if (isReset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setIsLoadingMore(true);
    }

    try {
      const currentPageNo = isReset ? 0 : pageNo;
      const request = createSportsHistoryRequest(currentPageNo, appliedFilters);
      const newData = await getSportsHistory(request);

      if (newData) {
        setSportsHistoryData((prev) => {
          if (isReset || !prev) {
            return newData;
          }
          return [...prev, ...newData];
        });
      }

      if (!newData || newData.length < 20) {
        setHasMore(false);
      }
      setPageNo((prev) => (isReset ? 1 : prev + 1));
    } finally {
      if (isReset) {
        setLoading(false);
        dispatch(changeLoaderState(false));
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const resetPagination = () => {
    setSportsHistoryData([]);
    setPageNo(0);
    setHasMore(true);

    // Reset UI state for open cards.
    setOpenRowIds([]);
  };

  const handleApply = async (appliedFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => {
    setFilters(appliedFilters);
    resetPagination();

    await fetchData(true, appliedFilters);
  };

  const handleCardToggle = async (
    item: SportsHistoryDetails,
    isOpen: boolean,
  ) => {
    if (isOpen) {
      dispatch(changeLoaderState(true));
      let data = await prepareBetHistory(item);
      if (data) {
        setBetHistoryData((prev) => ({
          ...prev,
          [item.betslipid]: data,
        }));
      }
      dispatch(changeLoaderState(false));
    } else {
      setBetHistoryData((prev) => {
        const { [item.betslipid]: _, ...rest } = prev;
        return rest;
      });
      setSysBetsArray(({ [item.betslipid]: _, ...rest }) => rest);
      setBetStrings(({ [item.betslipid]: _, ...rest }) => rest);
    }

    handleOpenRowIds(item.betslipid, isOpen);
  };

  useEffect(() => {
    const init = async () => {
      await fetchData(true);
    };

    init();
  }, []);

  const getBetType = (value?: string | null): string => {
    if (!value || !value.trim()) {
      return "";
    }

    if (SPORTS_BET_NAMES[value]) {
      return SPORTS_BET_NAMES[value];
    }

    if (value.includes("/")) {
      const betTypeArr = value.split("/");

      if (betTypeArr.length === 2) {
        const [first, second] = betTypeArr;

        if (first === second && second !== "1") {
          return `${SPORTS_BET_NAMES["ComboBet"]} ${first} Bets`;
        }

        if (first === second && second === "1") {
          return "";
        }

        return `${SPORTS_BET_NAMES["SystemBet"]} ${second} Bets`;
      }
    }
    return "";
  };

  const prepareBetHistory = async (item: SportsHistoryDetails) => {
    const data: BetHistory = {
      betslipId: item.betslipid,
      transactiondate: item.transactiondate,
      status: item.status,
    };

    item.transactiontype = getBetType(item.bettype);

    if (item.transactiontype) {
      data.transactiontype = item.transactiontype
        .toLowerCase()
        .includes("combo")
        ? SPORTS_BET_TYPES.COMBO
        : SPORTS_BET_TYPES.SYSTEM;
    } else {
      data.transactiontype = SPORTS_BET_TYPES.SINGLE;
    }

    data.betHistorydetails = await getBetDetails(item);

    if (data.transactiontype === SPORTS_BET_TYPES.SYSTEM) {
      let result = prepareForSystemBets(item, data);
      if (result && result.sysBetsArray.length > 0) {
        setSysBetsArray((prev) => ({
          ...prev,
          [item.betslipid]: result.sysBetsArray,
        }));
      }

      if (result && result.betStrings.length > 0) {
        setBetStrings((prev) => ({
          ...prev,
          [item.betslipid]: result.betStrings,
        }));
      }
    }
    return data;
  };

  const getBetDetails = async (
    item: SportsHistoryDetails,
  ): Promise<SportsHistorySummaryResponse[]> => {
    const request: BetByDetailsRequest = {
      betSlipId: item.betslipid,
      provider: item.provider,
    };

    return await getSportsBetHistoryDetails(request);
  };

  const generateCombinations = (
    selections: SportsHistorySummaryResponse[],
    choose: number,
  ): SportsHistorySummaryResponse[][] => {
    const results: SportsHistorySummaryResponse[][] = [];

    const backtrack = (
      start: number,
      combination: SportsHistorySummaryResponse[],
    ) => {
      if (combination.length === choose) {
        results.push([...combination]);
        return;
      }

      for (let i = start; i < selections.length; i++) {
        combination.push(selections[i]);
        backtrack(i + 1, combination);
        combination.pop();
      }
    };

    backtrack(0, []);
    return results;
  };

  const generateSystemBetCombinations = (
    selections: SportsHistorySummaryResponse[],
    bitmap: number[],
  ): SportsHistorySummaryResponse[][][] => {
    const systemGroups: SportsHistorySummaryResponse[][][] = [];

    for (let size = 1; size <= selections.length; size++) {
      if (bitmap[size - 1] === 1) {
        const combinations = generateCombinations(selections, size);
        systemGroups.push(combinations);
      }
    }

    return systemGroups;
  };

  const prepareForSystemBets = (
    betDetails: SportsHistoryDetails,
    allBets: BetHistory,
  ): {
    sysBetsArray: SportsHistorySummaryResponse[][];
    betStrings: string[];
  } => {
    const bitmap = SYSTEM_BITMAPS[betDetails.bettype];

    if (!bitmap) {
      throw new Error(`Unsupported bet type: ${betDetails.bettype}`);
    }

    const selections = [...(allBets.betHistorydetails ?? [])];

    if (selections.length > 11) {
      throw new Error("Selection exceeded (max 11 allowed)");
    }

    const groupedBets = generateSystemBetCombinations(selections, bitmap);

    const sysBetsArray = groupedBets.flat();

    const totalStake = selections[0]?.bet ?? 0;
    const perBetAmount = (totalStake / sysBetsArray.length).toFixed(2);

    const betStrings = groupedBets.map((group) => {
      const foldSize = group[0]?.length ?? 0;
      const foldName = FOLD_NAMES[foldSize] ?? `${foldSize}-fold`;
      return `${group.length} ${foldName} x ${perBetAmount}`;
    });

    return {
      sysBetsArray,
      betStrings,
    };
  };

  const renderBetComponent = (betDetails?: BetHistory | null) => {
    if (!betDetails) return null;

    switch (betDetails.transactiontype) {
      case SPORTS_BET_TYPES.SINGLE:
        return <SingleBet betDetails={betDetails} />;

      case SPORTS_BET_TYPES.COMBO:
        return <ComboBet betDetails={betDetails} />;

      case SPORTS_BET_TYPES.SYSTEM:
        if (!betDetails.betslipId) return null;
        return (
          <SystemBet
            betDetails={betDetails}
            sysBetsArray={sysBetsArray[betDetails.betslipId]}
            betStrings={betStrings[betDetails.betslipId]}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div>
        <TransactionFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={[
            localization.open,
            localization.win,
            localization.lost,
            localization.refund,
            localization.not_accepted,
            localization.half_win,
            localization.half_lost,
            localization.cashout,
          ]}
          onApply={handleApply}
        />
      </div>

      <InfiniteScroll
        onLoadMore={() => fetchData()}
        hasMore={hasMore}
        isLoading={loading || isLoadingMore}
      >
        {!loading && sportsHistoryData?.length === 0 && (
          <div className="text-xl flex justify-center mt-1">
            {localization.no_sports_history}
          </div>
        )}
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {sportsHistoryData?.length > 0 &&
            sportsHistoryData?.map((item) => (
                <div key={item.betslipid}>
                  <TransactionCard
                    onToggleChange={(isOpen) => handleCardToggle(item, isOpen)}
                  >
                    <div className="grid gap-2 text-white">
                      <div className="flex items-center text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-base xl:landscape:text-sm justify-between font-medium text-[#A9A9A9]">
                        <span>
                          {formatUtcToLocalDateTime(item.transactiondate)}
                        </span>
                        <span
                          className={`font-medium p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm xl:landscape:text-sm rounded-sm ${SPORTS_STATUS_STYLE[
                            item.status.trim().toLowerCase().replace(" ", "_")
                            ]
                            }`}
                        >
                          {localization.getString(
                            item.status.trim().toLowerCase().replace(" ", "_"),
                          )}
                        </span>
                      </div>

                      <div className="font-medium text-sm text-white">
                        {(() => {
                          let betType = getBetType(item.bettype);

                          if (!betType) {
                            betType = item.marketname;

                            return (
                              <div>
                                <div className="grid grid-cols-6">
                                  <div className="col-span-5 flex items-center">
                                    <img
                                      src={
                                        item.sportname
                                          ? `/icons/sports/sport-name/sport-${item.sportname.toLowerCase().replace(" ", "").replace(":", "-")}.svg`
                                          : "/icons/ic-tanis.svg"
                                      }
                                      alt="Sports Name"
                                      className="size-5 invert inline mr-1"
                                      onError={(e) => {
                                        const target = e.currentTarget;
                                        target.onerror = null;
                                        target.src =
                                          "/icons/sports-icon-active.svg";
                                      }}
                                    />
                                    <span className="text-base text-[#ECECFF] truncate">
                                      {item.competitorname}
                                    </span>
                                  </div>

                                  <div className="text-right">
                                    {`0.00\u20B9`}
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <span>{betType}</span>
                                </div>
                              </div>
                            );
                          } else {
                            return <span>{betType}</span>;
                          }
                        })()}
                      </div>

                      {openRowIds.includes(item.betslipid) && (
                        <div className="grid grid-rows-1 border-t border-[#353535] mt-2 pt-3 font-medium text-sm text-[#A9A9A9]">
                          {renderBetComponent(betHistoryData[item.betslipid])}
                        </div>
                      )}
                    </div>
                  </TransactionCard>
                </div>
            ))}
        </div>
      </InfiniteScroll>
    </div>
  );
}
